/**
 * Site header behaviour (snippets/header-menu.liquid and friends).
 *
 *   <header-popover>         hover/focus dropdowns for About us and My account
 *   <header-search-overlay>  Unboxed search. From 990px the compact field in
 *                            the bar is the real input: focusing it morphs the
 *                            field into a centred input, fades the rest of the
 *                            bar out, drops a panel of popular categories and
 *                            recent searches beneath and dims the page. Below
 *                            990px a full-screen overlay with its own input.
 *                            Recent searches: ./header/recent-searches.js.
 *
 * The navigation drawer is not here: it is Dawn's <header-drawer> in
 * assets/global.js plus the accordion animator in ./menu-drawer-animation.js.
 * The sticky offsets other elements read are published by ./sticky-stack.js.
 * The customer state that picks the CTA is resolved server-side
 * (snippets/customer-state.liquid); when that leaves a logged-in customer as
 * `prospect`, ./header/customer-state.js asks the SBS middleware and
 * applyCustomerState() rewrites every consumer in place.
 */

import { createRecentSearches } from "./header/recent-searches.js";
import { createCustomerStateResolver } from "./header/customer-state.js";

const DESKTOP = window.matchMedia("(min-width: 990px)");
const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");
const POPOVER_CLOSE_DELAY_MS = 150;
const POPOVER_END_CLASS = "header-popover--end";
const POPOVER_VIEWPORT_MARGIN = 8;
const RAISED_CLASS = "section-header--raised";
const SEARCH_OPEN_CLASS = "site-header--search-open";
const SCROLL_LOCK_CLASS = "search-lock";
const TABS_HIDDEN_CLASS = "site-header--tabs-hidden";
// The band may only slide away once the page has scrolled past its own
// height, so its slot always shows content rather than the header's empty
// in-flow space near the top; small deltas are ignored as jitter.
const TABS_HIDE_AFTER = 96;
const TABS_SCROLL_DELTA = 4;

const debugLog = (...args) => {
  if (window.smDebug) console.log("header", ...args);
};

/**
 * Lift the header section above page-level sticky helpers while a popover or
 * the search is showing (CSS: .section-header--raised). The drawer is covered
 * separately by HeaderDrawer's `menu-open`.
 */
function syncRaisedHeader() {
  const section = document.querySelector(".section-header");
  if (!section) return;
  const open = document.querySelector(
    "header-popover [data-popover-panel]:not([hidden]), header-search-overlay:not([hidden])"
  );
  section.classList.toggle(RAISED_CLASS, Boolean(open));
}

/**
 * Lock page scroll behind the search. The body is padded by the scrollbar's
 * width while locked so the header and content do not shift when it goes.
 */
const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex^="-"])';

/** Focusable descendants a Tab press can actually reach: rendered and visible. */
function tabbableWithin(scope) {
  return Array.from(scope.querySelectorAll(TABBABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden"
  );
}

function lockScroll(on) {
  const root = document.documentElement;
  if (on) {
    root.style.setProperty("--scroll-lock-gutter", `${window.innerWidth - root.clientWidth}px`);
    document.body.classList.add(SCROLL_LOCK_CLASS);
  } else {
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    root.style.removeProperty("--scroll-lock-gutter");
  }
}

class HeaderPopover extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector("[data-popover-trigger]");
    this.panel = this.querySelector("[data-popover-panel]");
    if (!this.trigger || !this.panel) return;

    this.closeTimer = null;
    this.hovering = false;
    this.touchOpensFirst = false;
    this.onDocumentClick = this.onDocumentClick.bind(this);

    // Hover opens for mouse and pen; a tap is a click (below).
    this.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "touch") return;
      this.hovering = true;
      this.open();
    });
    this.addEventListener("pointerleave", (event) => {
      if (event.pointerType === "touch") return;
      this.hovering = false;
      this.scheduleClose();
    });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    });
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.isOpen) return;
      event.preventDefault();
      this.close();
      this.trigger.focus();
    });
    if (this.trigger.tagName === "BUTTON") {
      // Disclosure button (About). Keyboard focus opens it so Tab exposes the
      // links; a tap focuses without :focus-visible and opens on its click
      // instead. Activating an already-open panel from the keyboard moves
      // focus into it rather than snapping it shut; a click over the panel
      // hover opened keeps it open; any other click on an open panel closes.
      this.trigger.addEventListener("focus", () => {
        if (this.trigger.matches(":focus-visible")) this.open();
      });
      this.trigger.addEventListener("click", () => {
        if (!this.isOpen) this.open();
        else if (this.trigger.matches(":focus-visible")) this.panel.querySelector("a[href], button")?.focus();
        else if (!this.hovering) this.close();
      });
    } else {
      // Link trigger (My account): focus opens so keyboard users can Tab into
      // the panel before the link navigates on Enter. Touch has no hover, so
      // at 990px+ the first tap opens the panel instead of following the link
      // and the next tap, with the panel open, navigates.
      this.addEventListener("focusin", () => this.open());
      this.trigger.addEventListener("pointerdown", (event) => {
        this.touchOpensFirst = event.pointerType === "touch" && !this.isOpen && DESKTOP.matches;
      });
      this.trigger.addEventListener("click", (event) => {
        if (!this.touchOpensFirst) return;
        this.touchOpensFirst = false;
        event.preventDefault();
        this.open();
      });
    }
    document.addEventListener("click", this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  open() {
    // Below 990px the same links live in the drawer; the trigger is a plain
    // icon/link there and the panel must not flash open on tap.
    if (!DESKTOP.matches) return;
    // Only one panel at a time: moving between triggers must not leave the
    // previous panel visible through its close grace period.
    document.querySelectorAll("header-popover").forEach((other) => {
      if (other !== this && typeof other.close === "function") other.close();
    });
    clearTimeout(this.closeTimer);
    this.panel.hidden = false;
    this.place();
    this.trigger.setAttribute("aria-expanded", "true");
    syncRaisedHeader();
  }

  /**
   * Open to the right of the trigger when that fits in the viewport; otherwise
   * hang from the trigger's right edge so the panel sits mostly to its left.
   * If neither fits, keep whichever side overflows less.
   */
  place() {
    const limit = document.documentElement.clientWidth - POPOVER_VIEWPORT_MARGIN;
    this.panel.classList.remove(POPOVER_END_CLASS);
    const overflowRight = this.panel.getBoundingClientRect().right - limit;
    if (overflowRight <= 0) return;

    this.panel.classList.add(POPOVER_END_CLASS);
    const overflowLeft = POPOVER_VIEWPORT_MARGIN - this.panel.getBoundingClientRect().left;
    if (overflowLeft > overflowRight) this.panel.classList.remove(POPOVER_END_CLASS);
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), POPOVER_CLOSE_DELAY_MS);
  }

  close() {
    clearTimeout(this.closeTimer);
    this.panel.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
    syncRaisedHeader();
  }
}

class HeaderSearchOverlay extends HTMLElement {
  connectedCallback() {
    this.header = this.closest(".site-header");
    // Desktop: the compact field in the bar, its input, the Cancel button and
    // the page scrim (all rendered by header-menu.liquid for the unboxed variant).
    this.field = this.header?.querySelector(".site-header__search-field") || null;
    this.fieldInput = this.field?.querySelector(".header-search__input") || null;
    this.cancel = this.header?.querySelector(".site-header__search-cancel") || null;
    this.scrim = this.header?.querySelector(".site-header__scrim") || null;
    // Mobile: the overlay's own input row.
    this.overlayInput = this.querySelector(".header-search__input");

    this.group = this.querySelector("[data-recent-searches]");
    this.list = this.querySelector("[data-recent-searches-list]");
    this.template = this.querySelector("[data-recent-search-template]");
    this.searchUrl = this.dataset.searchUrl || "/search";
    this.removeLabel = this.dataset.removeLabel || "";
    this.recent = createRecentSearches(storageOrNone("localStorage"));
    this.opener = null;
    this.desktopMode = false;
    this.ignoreFieldFocus = false;

    this.querySelectorAll("[data-search-close]").forEach((button) => {
      button.addEventListener("click", () => this.close());
    });
    this.cancel?.addEventListener("click", () => this.close());
    this.scrim?.addEventListener("click", () => this.close());
    document.querySelectorAll("[data-search-open]").forEach((button) => {
      button.addEventListener("click", () => this.open(button));
    });

    // The compact field is the real desktop input: focusing it, clicking it or
    // typing into it opens the panel around it.
    if (this.fieldInput) {
      const openFromField = () => {
        if (!this.ignoreFieldFocus && DESKTOP.matches && this.hidden) this.open(this.fieldInput);
      };
      this.fieldInput.addEventListener("focus", openFromField);
      this.fieldInput.addEventListener("input", openFromField);
      this.field.addEventListener("click", openFromField);
    }

    document.addEventListener("keydown", (event) => {
      if (this.hidden) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
      } else if (event.key === "Tab") {
        this.wrapTab(event);
      }
    });
    this.list?.addEventListener("click", (event) => {
      const remove = event.target.closest("[data-recent-search-remove]");
      if (!remove) return;
      this.recent.remove(remove.dataset.term);
      this.render();
    });

    document.querySelectorAll(".header-search__form").forEach((form) => this.bindForm(form));

    // Crossing 990px swaps which input owns the search; start over closed.
    DESKTOP.addEventListener("change", () => {
      if (!this.hidden) this.close({ restoreFocus: false });
    });

    this.render();
  }

  /** Clear button and recent-search recording for one search form. */
  bindForm(form) {
    const input = form.querySelector(".header-search__input");
    const reset = form.querySelector(".header-search__reset");
    if (!input) return;
    const sync = () => reset?.classList.toggle("hidden", input.value.length === 0);
    input.addEventListener("input", sync);
    reset?.addEventListener("click", () => {
      input.value = "";
      sync();
      input.focus();
    });
    form.addEventListener("submit", () => {
      if (input.value.trim()) this.recent.add(input.value);
    });
    sync();
  }

  open(opener) {
    if (!this.hidden) return;
    this.opener = opener || null;
    this.desktopMode = DESKTOP.matches && Boolean(this.field);
    lockScroll(true);
    this.hidden = false;
    this.render();
    if (this.desktopMode) {
      this.morph(() => this.header.classList.add(SEARCH_OPEN_CLASS));
      this.fieldInput.setAttribute("aria-expanded", "true");
      this.fieldInput.focus({ preventScroll: true });
    } else {
      // Full-screen takeover: a modal dialog. From 990px the panel is not a
      // dialog in ARIA terms (the field that owns focus sits outside it in the
      // bar), but Tab still wraps within the header while the scrim is up.
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.opener?.setAttribute("aria-expanded", "true");
      this.overlayInput?.focus();
    }
    syncRaisedHeader();
  }

  /**
   * Keep Tab inside the open search: the takeover below 990px, or the header
   * (logo, field, Cancel, panel) from 990px. Tabbable elements are computed
   * per keypress, so re-rendered recent searches and the bar's faded controls
   * need no bookkeeping, and focus that has left the scope (a scrim click)
   * comes back on the next Tab.
   */
  wrapTab(event) {
    const scope = this.desktopMode ? this.header : this;
    const items = tabbableWithin(scope);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !scope.contains(active);
    const atEdge = event.shiftKey ? active === first : active === last;
    if (!atEdge && !outside) return;
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }

  close({ restoreFocus = true } = {}) {
    if (this.hidden) return;
    this.hidden = true;
    lockScroll(false);
    if (this.desktopMode) {
      this.morph(() => this.header.classList.remove(SEARCH_OPEN_CLASS));
      this.fieldInput.setAttribute("aria-expanded", "false");
      if (restoreFocus) {
        // Hand focus back to the compact field without reopening the panel.
        this.ignoreFieldFocus = true;
        this.fieldInput.focus({ preventScroll: true });
        this.ignoreFieldFocus = false;
      }
    } else {
      this.removeAttribute("role");
      this.removeAttribute("aria-modal");
      this.opener?.setAttribute("aria-expanded", "false");
      if (restoreFocus) this.opener?.focus();
    }
    syncRaisedHeader();
  }

  /**
   * FLIP the compact field between its slot and the centred open position:
   * measure before and after the state change, replay the difference as an
   * inverse transform and width with transitions off, then release so the CSS
   * transitions carry it into the new layout. The slot keeps its box in the
   * row, so nothing else in the bar moves.
   */
  morph(applyState) {
    const field = this.field;
    const first = field.getBoundingClientRect();
    applyState();
    if (REDUCED_MOTION.matches) return;
    const last = field.getBoundingClientRect();

    field.style.transition = "none";
    field.style.width = `${first.width}px`;
    field.style.transform = `translate(${first.left - last.left}px, ${first.top - last.top}px)`;
    void field.offsetWidth;
    field.style.transition = "";
    requestAnimationFrame(() => {
      field.style.width = "";
      field.style.transform = "";
    });
  }

  render() {
    if (!this.group || !this.list || !this.template) return;
    const terms = this.recent.list();
    this.group.hidden = terms.length === 0;
    this.list.replaceChildren(...terms.map((term) => this.renderTerm(term)));
  }

  renderTerm(term) {
    const item = this.template.content.firstElementChild.cloneNode(true);
    const link = item.querySelector("[data-recent-search-link]");
    const remove = item.querySelector("[data-recent-search-remove]");

    link.textContent = term;
    link.href = `${this.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
    remove.dataset.term = term;
    remove.querySelector("[data-recent-search-remove-label]").textContent = this.removeLabel.replace("%TERM%", term);

    return item;
  }
}

/**
 * Below 990px the Stately | Unboxed band slides up behind the bar when the
 * page scrolls down and returns as soon as it scrolls up. The band keeps its
 * layout box (CSS moves and clips it), so the header never reflows; the
 * painted height it gives up is reported to sticky-stack.js through
 * data-collapsed-height so pinned elements follow the bar instead.
 */
function initTabsBand() {
  const header = document.querySelector(".site-header");
  const tabs = header?.querySelector(".site-header__tabs");
  const wrapper = header?.closest(".header-wrapper");
  if (!header || !tabs || !wrapper) return () => {};

  let lastY = window.scrollY;
  let hidden = false;
  let frame = 0;

  const setHidden = (next) => {
    if (next === hidden) return;
    hidden = next;
    header.classList.toggle(TABS_HIDDEN_CLASS, next);
    // The band's padding + tabs leave the painted header; its 1px bottom rule
    // (line-separator setting) stays put as the bar's bottom edge.
    const collapsed = next ? tabs.clientHeight : 0;
    wrapper.dataset.collapsedHeight = String(Math.round(collapsed));
    document.dispatchEvent(new CustomEvent("sticky-stack:refresh"));
  };

  const onScroll = () => {
    frame = 0;
    const y = window.scrollY;
    const delta = y - lastY;
    lastY = y;
    if (DESKTOP.matches || y <= TABS_HIDE_AFTER || delta < -TABS_SCROLL_DELTA) setHidden(false);
    else if (delta > TABS_SCROLL_DELTA) setHidden(true);
  };

  const onScrollEvent = () => {
    if (!frame) frame = requestAnimationFrame(onScroll);
  };
  const onDesktopChange = () => setHidden(false);
  window.addEventListener("scroll", onScrollEvent, { passive: true });
  DESKTOP.addEventListener("change", onDesktopChange);

  // Disposer for the theme editor, which replaces the header section (and so
  // these elements) on every setting change.
  return () => {
    cancelAnimationFrame(frame);
    window.removeEventListener("scroll", onScrollEvent);
    DESKTOP.removeEventListener("change", onDesktopChange);
  };
}

/**
 * Push a resolved customer state into everything the server rendered from it:
 * CTA copy/links (from the data-cta-{state}-text|url attributes header-cta
 * renders for logged-in customers), both data-customer-state attributes,
 * statelyVars and the dataLayer variable - a second push updates the DLV
 * (library/analytics/DataLayer-Guide.md, section 0b).
 */
function applyCustomerState(state) {
  document.body.dataset.customerState = state;
  document.querySelectorAll("sticky-header[data-customer-state]").forEach((element) => {
    element.dataset.customerState = state;
  });

  const key = state.charAt(0).toUpperCase() + state.slice(1);
  document.querySelectorAll("[data-cta-state]").forEach((cta) => {
    const text = cta.dataset[`cta${key}Text`];
    const url = cta.dataset[`cta${key}Url`];
    if (text === undefined) return;
    // A blank label means "no button in this state" (see header-cta.liquid).
    cta.hidden = text.trim() === "";
    cta.textContent = text;
    cta.href = url || "";
    cta.dataset.ctaState = state;
  });

  const vars = (window.statelyVars = window.statelyVars || {});
  vars.customerState = state;
  if (vars.customer) vars.customer.state = state;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ customer_state: state });
}

// The Storage getters themselves throw when storage is blocked (sandboxed or
// privacy settings), before any getItem/setItem the modules already guard.
function storageOrNone(name) {
  try {
    return window[name];
  } catch {
    return undefined;
  }
}

async function resolveCustomerState() {
  const vars = window.statelyVars || {};
  const resolver = createCustomerStateResolver({
    apiBase: vars.api?.public,
    storage: storageOrNone("sessionStorage"),
    log: debugLog,
  });
  // Subscriptions change on the pages with the logo-only header (portal,
  // plans, reactivation, abandon-cart) or in the checkout they hand off to.
  // Drop the cache when the customer leaves one of those pages so the next
  // storefront page re-queries; the global hook is for any other flow.
  window.smCustomerState = { invalidate: resolver.invalidate };
  if (document.querySelector("sticky-header")?.dataset.variant === "simple") {
    window.addEventListener("pagehide", resolver.invalidate);
  }

  const serverState = vars.customerState;
  const { state, source } = await resolver.resolve({ serverState, customerId: vars.customer?.id });
  debugLog("customer state", { serverState, state, source });
  if (state !== serverState) {
    resolvedState = state;
    applyCustomerState(state);
  }
}

let disposeTabsBand = () => {};
let resolvedState = null;

function init() {
  disposeTabsBand = initTabsBand();
  resolveCustomerState();
}

// Theme editor: the header section is re-rendered wholesale on every setting
// change, so rebind the band to the new elements and re-apply the state the
// browser resolved (the new markup carries the server state again).
document.addEventListener("shopify:section:load", (event) => {
  if (!event.target?.querySelector?.(".site-header")) return;
  disposeTabsBand();
  disposeTabsBand = initTabsBand();
  if (resolvedState) applyCustomerState(resolvedState);
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

if (!customElements.get("header-popover")) customElements.define("header-popover", HeaderPopover);
if (!customElements.get("header-search-overlay")) {
  customElements.define("header-search-overlay", HeaderSearchOverlay);
}
